import { DEFAULT_CONFIG } from './Config/default';
import { CellItem } from './RecyclerGridView/types';
import { EPGChannels, EPGRequestPriority } from './types';
import { idIsLoadingCellId, idIsNoDataCellId } from './utils';

// This class is resposible for mapping for which channels we have already requested data for.
// It receives the index updates (visible, engaged) checks if contains loading cells and requests
// based on what we(already have in the map (visible -> high prio, engaged -> normal prio).
//
// It also receives user interactions (scroll: both directions, selection: tap on mobile, focus on TV).
// After idlePeriod passes, we start to fetch nearest channels around visible ones which are not yet loaded
// with low priority (for pre-loading purpose).
//
// In the map also stores the priority, so in example if for a channel was requested low prio request
// because user being idle, if the user start scrolling and channel gets in viewport it will request
// again with high prio (same applies for high <-> normal or normal <-> low)
//
// requestMap: stores the map (key: channel id, value: request priority)
// channels: the list of the channels to be able to identify them
// dataProvider: the cell items, to be able to detect loading ones
// idlePeriod: the timeout value for idleTimer
//             period which if passes we consider the user being idle (not doing anything)
// idleTimer: the timeout to start pre-loading (low prio requests)
// needDataDelay: the timeout value for needDataTimer, to not fetch everything on fast scrolling,
//                only when user stops for this period (debouncing)
// needDataTimer: the timeout for firing the need data callback (for visible & engaged channels)
// needProgramsForChannels: the callback to request programs for channels with priority
//                          the returned value indicates if request was taken into account

class DataRequester {
    private _requestMap: Record<string, EPGRequestPriority> = {};

    private _channels: EPGChannels<any> = [];

    private _dataProvider: CellItem[][] = [];

    private _firstVisibleIndex: number = 0;

    private _lastVisibleIndex: number = 0;

    public preloadEnabled: boolean = DEFAULT_CONFIG.preloadEnabled;

    private _preloadDone: boolean = false;

    private _idleTimer?: NodeJS.Timeout;

    public idlePeriod: number = DEFAULT_CONFIG.idlePeriod;

    public idleRequestCount: number = DEFAULT_CONFIG.idleRequestCount;

    private _needDataTimer?: NodeJS.Timeout;

    public needDataDelay: number = DEFAULT_CONFIG.needDataDelay;

    public isActive: boolean = true;

    public needProgramsForChannels?: (
        channels: EPGChannels<any>,
        priority: EPGRequestPriority,
    ) => boolean;

    private _rowContainsLoading = (rowIndex: number) => {
        const rowData = this._dataProvider[rowIndex];
        let contains = false;

        for (let index = 0; index < rowData.length; index += 1) {
            if (idIsLoadingCellId(rowData[index].id)) {
                contains = true;
                break;
            }
        }

        return contains;
    };

    private _rowContainsNoData = (rowIndex: number) => {
        const rowData = this._dataProvider[rowIndex];
        let contains = false;

        for (let index = 0; index < rowData.length; index += 1) {
            if (idIsNoDataCellId(rowData[index].id)) {
                contains = true;
                break;
            }
        }

        return contains;
    };

    public update = (
        channels: EPGChannels<any>,
        dataProvider: CellItem[][],
    ) => {
        this._channels = channels;
        this._dataProvider = dataProvider;
        this.resetIdleTimer();
    };

    public onIndexesChanged = (
        visibleIndexes: number[][],
        engagedIndexes: number[][],
    ) => {
        clearTimeout(this._needDataTimer);
        this._needDataTimer = setTimeout(() => {
            let firstVisibleFound = false;
            const newVisibleChannels: EPGChannels<any> = [];
            visibleIndexes.forEach((row, rowIndex) => {
                const channel = this._channels[rowIndex];
                if (row.length) {
                    if (
                        (this._requestMap[channel.id] ?? 0) <
                            EPGRequestPriority.high &&
                        this._rowContainsLoading(rowIndex)
                    ) {
                        newVisibleChannels.push(channel);
                    } else if (
                        row.length === 1 &&
                        this._rowContainsNoData(rowIndex)
                    ) {
                        newVisibleChannels.push(channel);
                    }

                    if (!firstVisibleFound) {
                        firstVisibleFound = true;
                        this._firstVisibleIndex = rowIndex;
                    }
                    this._lastVisibleIndex = rowIndex;
                }
            });
            if (newVisibleChannels.length) {
                const processed = this.needProgramsForChannels?.(
                    newVisibleChannels,
                    EPGRequestPriority.high,
                );

                if (processed) {
                    newVisibleChannels.forEach((channel) => {
                        this._requestMap[channel.id] = EPGRequestPriority.high;
                    });
                }
            }

            if (this.preloadEnabled) {
                const newEngagedChannels: EPGChannels<any> = [];
                engagedIndexes.forEach((row, rowIndex) => {
                    const channel = this._channels[rowIndex];
                    if (
                        row.length &&
                        (this._requestMap[channel.id] ?? 0) <
                            EPGRequestPriority.normal &&
                        this._rowContainsLoading(rowIndex)
                    ) {
                        newEngagedChannels.push(channel);
                    }
                });
                if (newEngagedChannels.length) {
                    const processed = this.needProgramsForChannels?.(
                        newEngagedChannels,
                        EPGRequestPriority.normal,
                    );

                    if (processed) {
                        newEngagedChannels.forEach((channel) => {
                            this._requestMap[channel.id] =
                                EPGRequestPriority.normal;
                        });
                    }
                }
            }
        }, this.needDataDelay);
    };

    public resetIdleTimer = () => {
        clearInterval(this._idleTimer);

        if (!this.preloadEnabled || !this.isActive || this._preloadDone) {
            return;
        }

        this._idleTimer = setInterval(() => {
            let prevIndex = this._firstVisibleIndex - 1;
            let nextIndex = this._lastVisibleIndex + 1;
            const channelsToFetch: EPGChannels<any> = [];
            let prevDone = false;
            let nextDone = false;

            while (
                channelsToFetch.length < this.idleRequestCount &&
                (!prevDone || !nextDone)
            ) {
                if (prevIndex >= 0) {
                    const prevChannel = this._channels[prevIndex];
                    if (
                        !this._requestMap[prevChannel.id] &&
                        this._rowContainsLoading(prevIndex)
                    ) {
                        channelsToFetch.push(prevChannel);
                    }
                    prevIndex -= 1;
                } else {
                    prevDone = true;
                }

                if (nextIndex <= this._channels.length - 1) {
                    const nextChannel = this._channels[nextIndex];
                    if (
                        !this._requestMap[nextChannel.id] &&
                        this._rowContainsLoading(nextIndex)
                    ) {
                        channelsToFetch.push(nextChannel);
                    }
                    nextIndex += 1;
                } else {
                    nextDone = true;
                }
            }

            if (channelsToFetch.length) {
                const processed = this.needProgramsForChannels?.(
                    channelsToFetch,
                    EPGRequestPriority.low,
                );

                if (processed) {
                    channelsToFetch.forEach((channel) => {
                        this._requestMap[channel.id] = EPGRequestPriority.low;
                    });
                }
            } else {
                this._preloadDone = true;
                clearInterval(this._idleTimer);
            }
        }, this.idlePeriod);
    };

    public reset = () => {
        this._requestMap = {};
        this._preloadDone = false;
        this.resetIdleTimer();
    };
}

export default new DataRequester();
